<template>
  <section class="bg-white py-12">
    <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
      <h2 class="text-3xl font-bold tracking-tight text-gray-900">
        {{ title || $t('contact_form.title') }}
      </h2>
      <p v-if="subtitle" class="mt-2 text-gray-600">
        {{ subtitle }}
      </p>

      <div v-if="successMessage" class="mt-6 rounded-md bg-green-50 p-4 text-sm text-green-800">
        {{ successMessage }}
      </div>

      <form class="relative mt-8 grid grid-cols-1 gap-6" novalidate @submit.prevent="submit">
        <div
          v-if="errorMessages.length"
          ref="errorAlert"
          class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm leading-5 text-red-700"
          role="alert"
          tabindex="-1"
        >
          <svg class="mt-0.5 size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <ul v-if="errorMessages.length > 1" class="list-disc space-y-1 pl-4">
            <li v-for="(message, index) in errorMessages" :key="index">{{ message }}</li>
          </ul>
          <span v-else>{{ errorMessages[0] }}</span>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700" for="contact-form-name">{{ $t('contact_form.name') }}</label>
          <input
            id="contact-form-name"
            v-model="form.name"
            type="text"
            autocomplete="name"
            class="form-input mt-1 w-full"
            :class="fieldErrorClass('name')"
            :aria-invalid="!!errors.name"
            @input="clearFieldError('name')"
          >
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700" for="contact-form-email">{{ $t('contact_form.email') }}</label>
          <input
            id="contact-form-email"
            v-model="form.email"
            type="email"
            autocomplete="email"
            class="form-input mt-1 w-full"
            :class="fieldErrorClass('email')"
            :aria-invalid="!!errors.email"
            @input="clearFieldError('email')"
          >
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700" for="contact-form-message">{{ $t('contact_form.message') }}</label>
          <textarea
            id="contact-form-message"
            v-model="form.message"
            rows="5"
            :maxlength="MESSAGE_MAX"
            class="form-input mt-1 w-full"
            :class="fieldErrorClass('message')"
            :aria-invalid="!!errors.message"
            @input="clearFieldError('message')"
          />
          <div class="mt-1 text-right text-sm text-neutral-500">
            {{ form.message.length }}/{{ MESSAGE_MAX }}
          </div>
        </div>

        <div>
          <label class="flex cursor-pointer items-start gap-3">
            <input
              v-model="form.consent"
              type="checkbox"
              class="mt-1 size-[18px] shrink-0 rounded border-neutral-300 text-[#2ea8cf] focus:ring-[#2ea8cf]"
              @change="clearFieldError('consent')"
            >
            <span class="text-base font-medium leading-6 text-neutral-900">
              {{ $t('contact_form.consent') }}
            </span>
          </label>
        </div>

        <div>
          <button
            type="submit"
            :disabled="!canSubmit || processing"
            class="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)] disabled:cursor-not-allowed disabled:opacity-50"
            style="background-image: linear-gradient(176.294deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
          >
            <Icon
              v-if="!processing"
              name="check"
              class="size-5 shrink-0"
            />
            {{ processing ? $t('contact_form.sending') : (submitLabel || $t('contact_form.submit')) }}
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import Icon from '@/Shared/Icon.vue';

const MESSAGE_MAX = 240;

export default {
  name: 'ContactForm',
  components: { Icon },
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    submitLabel: { type: String, default: '' },
    // eslint-disable-next-line vue/prop-name-casing
    site_context: { type: String, default: null },
  },
  data() {
    return {
      MESSAGE_MAX,
      form: { name: '', email: '', message: '', consent: false },
      formStartedAt: Date.now(),
      errors: { name: '', email: '', message: '', consent: '' },
      formError: '',
      processing: false,
      successMessage: '',
    };
  },
  computed: {
    canSubmit() {
      return this.form.message.trim().length > 0 && this.form.consent;
    },
    errorMessages() {
      const messages = [];
      if (this.errors.name) messages.push(this.errors.name);
      if (this.errors.email) messages.push(this.errors.email);
      if (this.errors.message) messages.push(this.errors.message);
      if (this.errors.consent) messages.push(this.errors.consent);
      if (this.formError) messages.push(this.formError);
      return messages;
    },
  },
  methods: {
    fieldErrorClass(field) {
      return this.errors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : '';
    },
    clearFieldError(field) {
      if (this.errors[field]) this.errors[field] = '';
      this.formError = '';
    },
    clearErrors() {
      this.errors = { name: '', email: '', message: '', consent: '' };
      this.formError = '';
    },
    scrollToErrors() {
      this.$nextTick(() => {
        const alert = this.$refs.errorAlert;
        if (!alert) return;
        alert.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (typeof alert.focus === 'function') {
          alert.focus({ preventScroll: true });
        }
      });
    },
    async submit() {
      if (!this.canSubmit || this.processing) return;

      this.processing = true;
      this.clearErrors();
      this.successMessage = '';
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        const { data } = await axios.post('/api/contact-form', {
          contact: {
            ...this.form,
            site_context: this.site_context,
            form_started_at: this.formStartedAt,
          },
        }, { headers: { 'X-CSRF-Token': csrfToken } });
        this.successMessage = data.message || this.$t('contact_form.success');
        this.form = { name: '', email: '', message: '', consent: false };
        this.formStartedAt = Date.now();
        this.clearErrors();
      } catch (err) {
        const serverErrors = err.response?.data?.errors;
        if (serverErrors) {
          Object.keys(serverErrors).forEach((key) => {
            if (key in this.errors) this.errors[key] = serverErrors[key];
          });
          this.scrollToErrors();
        } else {
          this.formError = err.response?.data?.error || this.$t('contact_form.error');
          this.scrollToErrors();
        }
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>
